import React, { useEffect, useMemo, useState, useCallback } from "react";
import { connect } from "react-redux";
import { HiChevronLeft, HiChevronRight } from "react-icons/hi";
import { SALE_PRODUCT_LIST } from "../../constant";
import { GET } from "../../constant/apiMethods";
import Labels from "../../services/apiLabels";
import { useContext } from "react";
import FeedbackContext from "../../context/cmscontext";
import "./style.css";

const trim = (str) => (str || "").trim();

const CINEMATIC_BACKGROUNDS = [
  "/images/story-bg-1.jpg",
  "/images/story-bg-2.jpg",
  "/images/story-bg-3.jpg",
];

const FALLBACK_STORIES = [
  {
    id: "story-1",
    name: "Ahmed R.",
    vehicle: "Toyota GLI",
    quote:
      "A swift, transparent sale — from valuation to payment in under two hours. Exactly the experience I expected from a modern platform.",
    image: CINEMATIC_BACKGROUNDS[0],
  },
  {
    id: "story-2",
    name: "Abdullah K.",
    vehicle: "Toyota XLi",
    quote:
      "Every step was clear and professional. I received a fair market price without weeks of calls, visits, or uncertainty.",
    image: CINEMATIC_BACKGROUNDS[1],
  },
  {
    id: "story-3",
    name: "Sara M.",
    vehicle: "Honda City",
    quote:
      "Seamless paperwork, instant payment, and a team that genuinely cares. This is how selling a vehicle should feel.",
    image: CINEMATIC_BACKGROUNDS[2],
  },
];

function CustomerCard({ productList, productListStatus, SaleProductList }) {
  const { feedback } = useContext(FeedbackContext);
  const home_page = feedback.home_page;
  const [activeIndex, setActiveIndex] = useState(0);
  const [isAnimating, setIsAnimating] = useState(false);

  useEffect(() => {
    SaleProductList();
  }, []);

  const stories = useMemo(() => {
    if (productList?.length) {
      return productList.map((item, index) => ({
        id: item.id || `product-${index}`,
        name: trim(item.product_name) || "Verified client",
        vehicle: trim(item.product_name),
        quote: trim(item.product_description),
        image: CINEMATIC_BACKGROUNDS[index % CINEMATIC_BACKGROUNDS.length],
      }));
    }
    return FALLBACK_STORIES;
  }, [productList, productListStatus]);

  const displayStories = stories.length ? stories : FALLBACK_STORIES;
  const total = displayStories.length;
  const current = displayStories[activeIndex] || displayStories[0];

  const goTo = useCallback(
    (index) => {
      const next = ((index % total) + total) % total;
      if (next === activeIndex) return;
      setIsAnimating(true);
      setActiveIndex(next);
      window.setTimeout(() => setIsAnimating(false), 500);
    },
    [activeIndex, total]
  );

  const goNext = useCallback(() => goTo(activeIndex + 1), [activeIndex, goTo]);
  const goPrev = useCallback(() => goTo(activeIndex - 1), [activeIndex, goTo]);

  useEffect(() => {
    if (total <= 1) return undefined;
    const timer = window.setInterval(goNext, 9000);
    return () => window.clearInterval(timer);
  }, [goNext, total]);

  return (
    <div className="client-stories pi-stories-inner">
      <header className="pi-section-head">
        <p className="pi-label">Testimonials</p>
        <h2>{trim(home_page.happy_customers_heading)}</h2>
        <p>{trim(home_page.happy_customers_desp)}</p>
      </header>

      <div className="client-stories__stage coutomer_wrap card_wrap">
        <div className="client-stories__stage-media" aria-hidden>
          <div
            className={`client-stories__stage-bg ${isAnimating ? "client-stories__stage-bg--fade" : ""}`}
            style={{ backgroundImage: `url(${current.image})` }}
          />
          <div className="client-stories__stage-vignette" />
          <div className="client-stories__stage-overlay" />
          <div className="client-stories__stage-grain" />
        </div>

        <div className="client-stories__panel">
          <div className="client-stories__panel-surface" aria-hidden>
            <div className="client-stories__panel-glow" />
            <div className="client-stories__panel-lines" />
          </div>

          <div className={`client-stories__panel-body ${isAnimating ? "client-stories__panel-body--fade" : ""}`}>
            <span className="client-stories__stage-label">Client experience</span>
            <blockquote className="client-stories__stage-quote" key={current.id}>
              {current.quote}
            </blockquote>
            <footer className="client-stories__stage-cite">
              <strong className="customer_name">{current.name}</strong>
              <span className="client-stories__stage-divider" aria-hidden />
              <span>{current.vehicle}</span>
            </footer>
          </div>

          {total > 1 ? (
            <div className="client-stories__stage-nav">
              <button type="button" className="client-stories__nav-btn" onClick={goPrev} aria-label="Previous story">
                <HiChevronLeft />
              </button>

              <div className="client-stories__progress" role="tablist" aria-label="Stories">
                {displayStories.map((story, i) => (
                  <button
                    key={`${story.id}-${i === activeIndex ? activeIndex : "idle"}`}
                    type="button"
                    role="tab"
                    aria-selected={i === activeIndex}
                    aria-label={`Story ${i + 1}`}
                    className={`client-stories__progress-item ${i === activeIndex ? "is-active" : ""}`}
                    onClick={() => goTo(i)}
                  />
                ))}
              </div>

              <button type="button" className="client-stories__nav-btn" onClick={goNext} aria-label="Next story">
                <HiChevronRight />
              </button>
            </div>
          ) : null}
        </div>

        <div className="client-stories__visual-spacer" aria-hidden />
      </div>
    </div>
  );
}

const mapStateToProps = (state) => ({
  productList: state.cardetail.productlist,
  productListStatus: state.cardetail.productListStatus,
});

const mapDispatchToProps = (dispatch) => ({
  SaleProductList: () =>
    dispatch({
      type: SALE_PRODUCT_LIST,
      payload: {
        type: SALE_PRODUCT_LIST,
        method: GET,
        label: Labels.sale_product,
        hideOverlayLoader: true,
      },
    }),
});

export default connect(mapStateToProps, mapDispatchToProps)(CustomerCard);
